import * as React from "react";
import * as clientViews from "@app/admin/client-administration/views";
import { DashboardsProvider } from "@app/admin/contexts/DashboardsContext";
import { toast } from "react-toastify";
import { Link, Redirect } from "react-router-dom";
import { ClientContext, ClientProvider } from "@app/admin/contexts/ClientContext";
import { InsurancesProvider } from "@app/admin/contexts/InsurancesContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import Page from "@app/shared/Page";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";

const ClientCreationWithContext: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [error] = React.useState("");
  const clientContext = React.useContext(ClientContext);
  const [submitting, setSubmitting] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");

  const handleSubmit = () => {
    setSubmitting(true);
    clientContext.createClient().then(() => {
      toast(<span>Client successfully created!</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect("/app/v2.0/admin/client-administration");
          },
          type: "success",
        },
      );
    }, (e) => {
      toast(<span>We encountered a problem creating a new client: {e}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  const loading = (!clientContext.authorizedRoles || !clientContext.authorizedDashboards);

  return (
    <Page documentTitle="New Client - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.ClientAdministration} items={["New Client"]} />
      <PageInner>
        <PageHeader title={"New Client"} iconName={QesIcons.Briefcase} disableHR={false} />
        <div className="row mt-3">
          <div className="col-10">
            {!submitting && loading && <div className="mt-3"><LoadingSpinner /></div>}
            {!submitting && !loading && (
              <>
                <PageSection>
                  {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
                  <clientViews.ClientInfo />
                </PageSection>

                <clientViews.ClientDomainsAndDashboards />
                <clientViews.ClientRoles />
                <clientViews.ClientDefaultUserRoles />
                <InsurancesProvider availableInsurances={clientContext.availableInsurances}>
                  <clientViews.ClientAuthorizedInsurances />
                </InsurancesProvider>
              </>
            )}
            {submitting && (
              <div className="mt-3 text-center">
                <h2>Client creation is in progress. This may take a few minutes....</h2>
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
        <p className="text-muted small mt-3" data-testid="new-client__snowflake-provisioning-footnote">
          <i>
            Note: Creating a client here does not automatically provision its Snowflake resources.
            {" "}Submit a <a href="https://dev.azure.com/questanalytics/Operations%20Intake/_wiki/wikis/Operations-Intake.wiki/860/QES-Client-Setup?anchor=devops-snowflake-processing" target="_blank" rel="noreferrer">Snowflake provisioning request</a> so the client&apos;s data can be provisioned.
          </i>
        </p>
      </PageInner>
      <WizardNavigationBar
        leftSide={!submitting && <Link className="qes-button qes-button-secondary" to="/admin/client-administration">Cancel</Link>}
        rightSide={!submitting &&
          <>
            {clientContext.resourceAdminGuestLoginsAllowed || clientContext.questViewerGuestLoginsAllowed ?
              <QESCheckbox label="Create Guest Logins for Authorized Quest Users" testId="new-client__create-guest-logins" onChange={(e) => clientContext.setCreateNewClientGuestLogins(e.target.checked)} checked={clientContext.createNewClientGuestLogins} />
              : null}
            <QESButton
              qesType="primary"
              onClick={handleSubmit}
              qesTestId="new-client__create"
              disabled={!clientContext.formIsValid}
            >
              Create
            </QESButton>
          </>
        }
      />
    </Page >
  );
}

export const ClientCreation: React.FC<React.PropsWithChildren<unknown>> = () => (
  <ClientProvider>
    {/* These two are used by the children */}
    <DashboardsProvider>
      <ClientCreationWithContext />
    </DashboardsProvider>
  </ClientProvider>
);